// Client activity timeline for the staff console: audit events rendered as
// Czech sentences grouped by day, never as raw event codes. The partner reads
// "what happened around this client"; technical payloads stay admin-only.
// Pure helpers exported for tests.
import { AUDIT_TYPES } from "../shared/audit-catalog";
import type { Locale } from "../shared/dto";
import { clockFields, intlLanguage } from "./date-format";

export type ActivityRow = {
  id: string;
  type: string;
  createdAt: string;
  data: Record<string, unknown> | null;
  actorId?: string | null;
  actorName?: string | null;
};

type T = (key: string) => string;

// Every event type the system emits today (auth rate/link noise is filtered
// server-side). The locale-completeness test walks this list, so a new event
// type without a Czech sentence fails the suite instead of leaking raw codes.
//
// 🔴 F138 kolečko 2: this used to be a hand-typed list of 57 types living
// beside the classification the Servis timeline uses, and the two had drifted
// apart - 53 of the 109 types the product really writes were missing here, so
// `deliverable_published` fell through to the unknown branch and printed
// itself, underscores swapped for spaces, in the middle of a Czech sentence.
// One inventory now: `shared/audit-catalog.ts`, read by the classification and
// by this catalog, and a type added there fails the guard until it has a
// sentence in both locales.
export const ACTIVITY_TYPES: readonly string[] = AUDIT_TYPES;

// The automation user acts as Alex; the sentences already name him, so an
// actor chip would only repeat it.
const AUTOMATION_ACTOR_ID = "usr-automation-system";

function safeT(t: T): (key: string, fallback: string) => string {
  return (key, fallback) => {
    try {
      const value = t(key);
      return value === key ? fallback : value;
    } catch {
      return fallback;
    }
  };
}

// 🔴 A missing label is answered with NOTHING, never with the code itself.
// The old fallback swapped underscores for spaces and handed the result to the
// sentence, which is how "Systémová událost: deliverable published" and
// "Tým nastavil milník order links integrated" reached a partner's screen: an
// internal code wearing a Czech sentence. The sentence drops the parameter
// instead (it is written to read without it), the guard makes sure a label
// really exists for every key the product writes, and an admin still has the
// technical payload one fold below.
const NO_LABEL = "";

// The {what} parameter per event family: deliverable kind, questionnaire
// title, milestone label or resource name - always through its locale map.
function whatParam(row: ActivityRow, t: T): string {
  const safe = safeT(t);
  const data = row.data ?? {};
  const kind = typeof data.kind === "string" ? data.kind : typeof data.gate === "string" ? data.gate : null;
  if (row.type === "stable_call_link_redeemed" && (kind === "onboarding" || kind === "marketing")) {
    return safe(`staffConsole.stableCallKind.${kind}`, NO_LABEL);
  }
  if (row.type === "client_confirmation_reverted" && kind) {
    return safe(`staffConsole.confirmationUndo.kind.${kind}`, NO_LABEL);
  }
  if (kind) return safe(`sales.kind.${kind}`, NO_LABEL);
  if (row.type === "questionnaire_submitted" && typeof data.questionnaire === "string") {
    return safe(`sections.${data.questionnaire}.title`, NO_LABEL);
  }
  if (row.type === "milestone_set" && typeof data.key === "string") {
    return safe(`handover.proof.${data.key}`, NO_LABEL);
  }
  if (row.type === "resource_updated" && typeof data.key === "string") {
    return safe(`staffConsole.resourceName.${data.key}`, NO_LABEL);
  }
  return "";
}

export function activitySentence(row: ActivityRow, t: T): string {
  const safe = safeT(t);
  const template = safe(`staffConsole.clientActivity.${row.type}`, "");
  // An event this build has no sentence for says so in words. The type itself
  // stays out of the sentence and rides the admin-only technical fold, where a
  // code belongs (princip 5: no internal code on the screen).
  if (!template) return safe("staffConsole.clientActivityUnknown", "Zaznamenali jsme změnu u klienta.");
  const what = whatParam(row, t);
  // Older events may miss the payload field: drop the parameter with its
  // separator so no orphan ": ." or " ." survives.
  if (!what) return template.replace(": {what}", "").replace(" {what}", "").replace("{what}", "");
  return template.replace("{what}", what);
}

export type ActivityDay = { key: string; label: string; items: ActivityRow[] };

/**
 * 🔴 Grouping only ever compares a row with the one BEFORE it, so it groups
 * nothing unless the rows arrive sorted - and unsorted rows do not read as
 * unsorted, they read as a broken heading: measured NEDĚLE 9. → PONDĚLÍ 10. →
 * NEDĚLE 9. → SOBOTA 8., the same day titled twice, which says the history
 * itself is wrong. The server's audit query does order by `created_at DESC`,
 * but a surface that depends on somebody else's ORDER BY is a surface that
 * breaks the day a caller changes it, and the fixture proved it: the same
 * component, unsorted rows, two Sundays.
 *
 * So the pane sorts what it was given: newest first, stable within one moment,
 * and a row whose timestamp cannot be parsed sinks to the end rather than
 * landing in a group whose heading would then be a guess.
 */
export function groupActivity(rows: ActivityRow[], locale: Locale): ActivityDay[] {
  const fmt = new Intl.DateTimeFormat(intlLanguage(locale), {
    weekday: "long", day: "numeric", month: "long"
  });
  const stamp = (row: ActivityRow) => {
    const value = new Date(row.createdAt).getTime();
    return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
  };
  const groups: ActivityDay[] = [];
  for (const row of [...rows].sort((left, right) => stamp(right) - stamp(left))) {
    const day = new Date(row.createdAt);
    const key = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(row);
    else groups.push({ key, label: fmt.format(day), items: [row] });
  }
  return groups;
}

// Newest days stay visible, the long tail folds. Whole day groups only, so
// a day never splits across the fold.
export function splitActivityDays(groups: ActivityDay[], cap = 25): { visible: ActivityDay[]; older: ActivityDay[] } {
  const visible: ActivityDay[] = [];
  let count = 0;
  let index = 0;
  for (; index < groups.length; index++) {
    if (count >= cap) break;
    visible.push(groups[index]);
    count += groups[index].items.length;
  }
  return { visible, older: groups.slice(index) };
}

export function ActivityTimeline(props: {
  rows: ActivityRow[];
  locale: Locale;
  t: T;
  isAdmin: boolean;
}) {
  const { rows, locale, t, isAdmin } = props;
  if (!rows.length) return <p className="staff-empty">{t("staffConsole.emptyAudit")}</p>;
  const timeFmt = new Intl.DateTimeFormat(intlLanguage(locale), clockFields(locale));
  const { visible, older } = splitActivityDays(groupActivity(rows, locale));
  const olderCount = older.reduce((sum, group) => sum + group.items.length, 0);
  const renderDay = (group: ActivityDay) => (
    <section className="activity-day" key={group.key}>
      <h3>{group.label}</h3>
      <ol>
        {group.items.map((row) => {
          const actor = row.actorId === AUTOMATION_ACTOR_ID ? null : row.actorName ?? null;
          // The event's own type belongs to the admin's technical fold, never
          // to the sentence. It rides here so nothing is LOST by taking the
          // code off the screen: an admin debugging still reads it, one click
          // away, next to the payload it explains.
          const technical = { type: row.type, ...(row.data ?? {}) };
          return (
            <li className="activity-item" key={row.id}>
              <time dateTime={row.createdAt}>{timeFmt.format(new Date(row.createdAt))}</time>
              <div className="activity-body">
                <p>
                  {activitySentence(row, t)}
                  {actor && <span className="activity-actor"> · {actor}</span>}
                </p>
                {isAdmin && (
                  <details className="raw-details">
                    <summary>{t("staffConsole.rawData")}</summary>
                    <pre>{JSON.stringify(technical, null, 2)}</pre>
                  </details>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
  return (
    <div className="activity-timeline">
      {visible.map(renderDay)}
      {older.length > 0 && (
        <details className="activity-older">
          <summary>{t("staffConsole.clientActivityOlder").replace("{count}", String(olderCount))}</summary>
          {older.map(renderDay)}
        </details>
      )}
    </div>
  );
}
